import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import ReserveSpot from '../components/ReserveSpot';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getVertical } from '../common/verticals';

const { FiMapPin, FiClock, FiRefreshCw, FiFacebook, FiBell } = FiIcons;

export default function CityPage() {
  const { citySlug } = useParams();
  const [city, setCity] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    supabase
      .from('cities_1715634000000')
      .select('id, name, slug, is_active, vertical, state')
      .eq('slug', citySlug)
      .single()
      .then(({ data }) => setCity(data ?? null));
  }, [citySlug]);

  if (city === undefined) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <SafeIcon icon={FiRefreshCw} className="animate-spin text-4xl text-brand-blue" />
      </div>
    );
  }

  if (!city) return <Navigate to="/" replace />;

  const v = getVertical(city.vertical);
  const cityState = city.state || 'AZ';

  // Coming Soon — city exists but not active yet
  if (!city.is_active) {
    return (
      <div className="pt-24 min-h-screen bg-brand-dark flex flex-col">
        <section className="flex-1 flex items-center justify-center py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[140px]" style={{ backgroundColor: v.hex }} />
          </div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-brand-yellow text-brand-brown px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6">
              <SafeIcon icon={FiClock} /> Coming Soon
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter italic leading-none mb-6">
              {city.name}<br />
              <span style={{ color: v.hex }}>Is Next.</span>
            </h1>
            <p className="text-xl text-brand-cream/60 font-bold max-w-xl mx-auto mb-4 leading-relaxed">
              The {city.name} {v.collective} is filling up. Message Sean now to reserve your trade before this city opens — early inquiries get first pick of categories.
            </p>
            <div className="inline-flex items-center gap-2 text-brand-cream/40 font-black uppercase text-xs tracking-widest mb-12">
              <SafeIcon icon={FiMapPin} /> {city.name}, {cityState}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.facebook.com/thegyrohero"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 text-white font-black uppercase text-lg px-10 py-5 rounded-[2rem] transition-all hover:scale-105 active:scale-95 shadow-2xl"
                style={{ backgroundColor: v.hex }}
              >
                <SafeIcon icon={FiBell} /> Join the Waitlist
              </a>
              <a
                href="https://www.facebook.com/thegyrohero"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white/10 text-white font-black uppercase text-lg px-10 py-5 rounded-[2rem] border-2 border-white/20 hover:bg-white/20 transition-all"
              >
                <SafeIcon icon={FiFacebook} /> Message Sean
              </a>
            </div>

            <p className="text-brand-cream/20 text-xs font-bold uppercase tracking-widest mt-10">
              Already in {v.collective === 'Home Services Collective' ? 'Surprise' : 'another city'}? Ask about our multi-city rate.
            </p>
          </div>
        </section>
      </div>
    );
  }

  // Active city — show full slots
  return (
    <div className="pt-24 min-h-screen bg-brand-cream">
      <section className="bg-brand-dark py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1600&q=80"
            className="w-full h-full object-cover"
            alt=""
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-4 text-white" style={{ backgroundColor: v.hex }}>
            {v.label}
          </div>
          <div className="inline-flex items-center gap-2 bg-brand-yellow text-brand-brown px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6 ml-2">
            <SafeIcon icon={FiClock} /> Now Open
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter italic mb-6">
            {city.name} <span style={{ color: v.hex }}>Is Live</span>
          </h1>
          <p className="text-xl md:text-2xl text-brand-cream/70 font-bold max-w-2xl mx-auto">
            Claim your exclusive trade before the first {city.name} drop.
          </p>
        </div>
      </section>

      <div className="py-12">
        <div className="max-w-6xl mx-auto px-4 text-center mb-12">
          <div className="inline-flex items-center gap-3 font-black uppercase tracking-tighter text-2xl" style={{ color: v.hex }}>
            <SafeIcon icon={FiMapPin} /> {city.name}, {cityState}
          </div>
        </div>
        <ReserveSpot citySlug={city.slug} />
      </div>
    </div>
  );
}
